import * as services from '@api';
import { bNeedRefresh } from '../navigate';
import { requiredValidate, getHoverRow, batchByAsyncKeyTask } from '../utils';
import { isType } from '@src/utils';

/**
 * batchDo
 * @param rootStore 
 * @param options {
 *  // 必填字段
 *  dataSourceAlias: string
 *  action: string，值：stop|unstop
 *  // 选填字段
 *  billNo: string， 默认值：params.billNo
 *  mode: string
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }  
 */
export async function batchDo(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['dataSourceAlias', 'action']);
  const { dataSourceAlias = '', action = '', data = [], isAsync = false, title = '' } = options;
  const { utils = {}, pageStore = {} } = rootStore;
  const { params = {}, query = {} } = pageStore;
  const upperActionName = action.replace(/^\w/, ($0: any) => $0.toUpperCase());

  try {
    pageStore?.startLoading?.();
    let requestData = { 
      params: {
        ...query,
        action,
        datasource: dataSourceAlias,
        billnum: options.billNo || params.billNo,
        busiObj: options.busiObj || query.busiObj,
        mode: options.mode || pageStore.status
      },
      data: {
        data: JSON.stringify(data)
      }
    }
    if (isAsync) {
      Object.assign(requestData.data, {
        externalData: {
          isAsync: true
        }
      })
    }
    // before${upperActionName}Hook
    if (isType(this[`before${upperActionName}Hook`], 'Function')) {
      requestData = await this[`before${upperActionName}Hook`](rootStore, requestData);
    }
    // batchDo
    let resStep1 = await services.batchDo(requestData);

    if (isAsync) {
      batchByAsyncKeyTask.call(this, rootStore, { ...resStep1.data.infos[0], title });
      return resStep1;
    } else {
      // after${upperActionName}Hook
      if (isType(this[`after${upperActionName}Hook`], 'Function')) {
        resStep1 = await this[`after${upperActionName}Hook`](rootStore, resStep1);
      }
      utils.message.success(`${title} ${lang.templateByUuid('UID:P_CORE-YNF-FE_18A243FC05A8000D', '成功', undefined, { returnStr: true })}`);
      return resStep1;
    }
  } catch (err: any) {
    utils.message.danger(err?.message);
    return Promise.reject(err);
  } finally {
    pageStore?.endLoading?.();
  }
}

/**
 * 停用
 * @param rootStore 
 * @param options {
 *  // 必填字段
 *  cardStoreKey: string， 默认值：cardStore
 *  // 选填字段
 *  dataSourceAlias: string， 默认值：cardStore.dataSource
 *  billNo: string， 默认值：params.billNo
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }  
 */
export async function stop(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['cardStoreKey']);
  const { dataSourceAlias = '', cardStoreKey = 'cardStore' } = options;
  const cardStore = rootStore.get(cardStoreKey);
  if (!cardStore) throw (new Error(`The CardStore[${cardStoreKey}] not found`));
  try {
    const res = await batchDo.call(this, rootStore,  { 
      ...options, 
      dataSourceAlias: dataSourceAlias || cardStore?.dataSource, 
      action: 'stop', 
      title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80050', '停用', undefined, { returnStr: true }),
      mode: cardStore.uiState,
      data: [ cardStore.getValue() ] 
    });
    // 数据回写
    cardStore.setValue(res.data, { merge: true });
    return res;
  } catch (err: any) {
    return Promise.reject(err);
  }
}

/**
 * 启用
 * @param rootStore 
 * @param options {
 *  // 必填字段
 *  cardStoreKey: string， 默认值：cardStore
 *  // 选填字段
 *  dataSourceAlias: string， 默认值：cardStore.dataSource
 *  billNo: string， 默认值：params.billNo
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }  
 */
export async function unstop(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['cardStoreKey']);
  const { dataSourceAlias = '', cardStoreKey = 'cardStore' } = options;
  const cardStore = rootStore.get(cardStoreKey);
  if (!cardStore) throw (new Error(`The CardStore[${cardStoreKey}] not found`));
  try {
    const res = await batchDo.call(this, rootStore,  { 
      ...options, 
      dataSourceAlias: dataSourceAlias || cardStore?.dataSource, 
      action: 'unstop', 
      title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80051', '启用', undefined, { returnStr: true }),
      mode: cardStore.uiState,
      data: [ cardStore.getValue() ] 
    });
    // 数据回写
    cardStore.setValue(res.data, { merge: true });
    return res;
  } catch (err: any) {
    return Promise.reject(err);
  }
}

/**
 * 批量停用&行停用
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  dataSourceAlias: string
 *  tableStoreKey: string， 默认值：tableStore
 *  // 选填字段
 *  billNo: string， 默认值：params.billNo
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function batchStopByRow(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['dataSourceAlias', 'tableStoreKey']);
  const [ hoverRow, tableStore ] = getHoverRow.apply(this, arguments);
  const { utils = {} } = rootStore;
  try {
    if (hoverRow) {
      const res = await batchDo.call(this, rootStore,  { 
        ...options, 
        action: 'stop', 
        title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80050', '停用', undefined, { returnStr: true }),
        data: [ hoverRow.dataEntityToJson() ],
        mode: tableStore.uiState 
      });
      bNeedRefresh.apply(this, arguments);
      return res;
    } else {
      const selectRows = tableStore.getSelectedRows();
      if (!selectRows.length) {
        utils.message.warning(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80048', '目前没有选中行', undefined, { returnStr: true }));
        return Promise.reject(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80048', '目前没有选中行', undefined, { returnStr: true }));
      } else {
        const isAsync = selectRows.length > 1;
        const res = await batchDo.call(this, rootStore, { 
          ...options, 
          action: 'stop',
          title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80050', '停用', undefined, { returnStr: true }),
          data: selectRows.map((row: any) => row.dataEntityToJson()), 
          isAsync,
          mode: tableStore.uiState  
        });
        tableStore.clearMultiplePageSelectedData();
        if (!isAsync) bNeedRefresh.apply(this, arguments);
        return res;
      }
    }
  } catch (err: any) {
    return Promise.reject(err);
  }
}

/**
 * 批量启用&行启用
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  dataSourceAlias: string
 *  tableStoreKey: string， 默认值：tableStore
 *  // 选填字段
 *  billNo: string， 默认值：params.billNo
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function batchUnstopByRow(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['dataSourceAlias', 'tableStoreKey']);
  const [ hoverRow, tableStore ] = getHoverRow.apply(this, arguments);
  const { utils = {} } = rootStore;
  try {
    if (hoverRow) {
      const res = await batchDo.call(this, rootStore,  { 
        ...options, 
        action: 'unstop', 
        title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80051', '启用', undefined, { returnStr: true }),
        data: [ hoverRow.dataEntityToJson() ],
        mode: tableStore.uiState 
      });
      bNeedRefresh.apply(this, arguments);
      return res;
    } else {
      const selectRows = tableStore.getSelectedRows();
      if (!selectRows.length) {
        utils.message.warning(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80048', '目前没有选中行', undefined, { returnStr: true }));
        return Promise.reject(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80048', '目前没有选中行', undefined, { returnStr: true }));
      } else {
        const isAsync = selectRows.length > 1;
        const res = await batchDo.call(this, rootStore, { 
          ...options, 
          action: 'unstop', 
          title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80051', '启用', undefined, { returnStr: true }),
          data: selectRows.map((row: any) => row.dataEntityToJson()), 
          isAsync,
          mode: tableStore.uiState 
        });
        tableStore.clearMultiplePageSelectedData();
        if (!isAsync) bNeedRefresh.apply(this, arguments);
        return res;
      }
    }
  } catch (err: any) {
    return Promise.reject(err);
  }
}
